/**
 * @lines98/client/services/replay-store — localStorage persistence for finished games.
 *
 * T-5 scope (per brief): «После endGame — сохрани партию в localStorage (JSON
 * { seed, moves, mode, engineVersion, score, finishReason }). На "Watch Replay"
 * — загрузи и запусти ReplayScreen.»
 *
 * SHAPE:
 *   - The saved record extends the canonical `Game` (seed + moves[] + mode) with
 *     local-only fields needed for the Result screen to render and the Replay
 *     screen to start from the same state. We DON'T re-derive the canonical
 *     shape — `seed + moves[]` is enough; score / lines / finishReason are echo
 *     for UI convenience.
 *
 * KEY DESIGN:
 *   - Single «last game» slot under `LAST_GAME_KEY`. T-5 brief: local-only
 *     fixture; no archive. (Multiple-game library is T-7 / Pro territory.)
 *   - JSON.stringify / JSON.parse with strict validation on load — corrupted
 *     blobs return null без бросания.
 *
 * SECURITY:
 *   - localStorage is per-origin. No PII. Saved blob is just `seed + moves[]` —
 *     all reproducible via `replayScore` (anti-cheat.md §3).
 */

import type { FinishReason, GameMode, Move } from '@lines98/shared/types/game';

const LAST_GAME_KEY = 'lines98.last-game.v1';

/**
 * A saved finished game (local-only). Aligns with `Game` shape from
 * `@lines98/shared/types/game.ts` (seed + moves[] + mode) plus local UX echo.
 */
export interface SavedGame {
  /** Schema version — bump on shape change. */
  readonly v: 1;
  readonly seed: number;
  readonly moves: Move[];
  readonly mode: GameMode;
  readonly score: number;
  readonly lines: number;
  readonly finishReason: FinishReason;
  readonly engineVersion: string;
  /** Local epoch ms — for «played at» UX. */
  readonly finishedAtMs: number;
}

/**
 * Save the last finished game (overwrites any previous «last»). Best-effort —
 * if localStorage is unavailable (quota / private mode), silently no-op.
 */
export function saveLastGame(record: SavedGame): void {
  try {
    window.localStorage.setItem(LAST_GAME_KEY, JSON.stringify(record));
  } catch {
    // localStorage quota / private mode / disabled — non-fatal.
  }
}

/**
 * Load the last finished game (or null if none / corrupted).
 */
export function loadLastGame(): SavedGame | null {
  try {
    const raw = window.localStorage.getItem(LAST_GAME_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (!isSavedGame(parsed)) return null;
    return parsed;
  } catch {
    return null;
  }
}

/** Forget the last game (e.g., user explicitly cleared, or schema mismatch). */
export function clearLastGame(): void {
  try {
    window.localStorage.removeItem(LAST_GAME_KEY);
  } catch {
    // ignore
  }
}

// =============================================================================
// Type guards (defence against tampered / mis-versioned JSON)
// =============================================================================

function isSavedGame(value: unknown): value is SavedGame {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    v['v'] === 1 &&
    typeof v['seed'] === 'number' &&
    Array.isArray(v['moves']) &&
    (v['moves'] as unknown[]).every(isMove) &&
    isGameMode(v['mode']) &&
    typeof v['score'] === 'number' &&
    typeof v['lines'] === 'number' &&
    isFinishReason(v['finishReason']) &&
    typeof v['engineVersion'] === 'string' &&
    typeof v['finishedAtMs'] === 'number'
  );
}

function isMove(value: unknown): value is Move {
  if (!value || typeof value !== 'object') return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v['sr'] === 'number' &&
    typeof v['sc'] === 'number' &&
    typeof v['r'] === 'number' &&
    typeof v['c'] === 'number'
  );
}

function isGameMode(value: unknown): value is GameMode {
  return value === 'predict' || value === 'classic' || value === 'blind';
}

function isFinishReason(value: unknown): value is FinishReason {
  // Accept every persisted reason — incl. `restart` (restart-full-completion.md:
  // «Новая игра» сохраняется локально как полный финиш) и legacy `new_game` /
  // зарезервированный `timeout` (forward-compat). Иначе валидный record отбрасывался бы.
  return (
    value === 'board_full' ||
    value === 'player_finish' ||
    value === 'rollover' ||
    value === 'new_game' ||
    value === 'restart' ||
    value === 'timeout'
  );
}
